/**
 * Product reviews as the backend (`iws-commerce-server`) serves them. Every submission waits for approval:
 * `pending` → `active` (approved, public, counted in the rating) or `rejected`. Only verified buyers (a delivered
 * or completed order with the product) can review, once per product; editing sends it back to `pending`.
 */

export const REVIEW_STATUS_PENDING = "pending";
export const REVIEW_STATUS_APPROVED = "active";
export const REVIEW_STATUS_REJECTED = "rejected";
export type ReviewStatus = typeof REVIEW_STATUS_PENDING | typeof REVIEW_STATUS_APPROVED | typeof REVIEW_STATUS_REJECTED;

export const REVIEW_SORTS = ["newest", "highest", "lowest", "helpful"] as const;
export type ReviewSort = (typeof REVIEW_SORTS)[number];

export const REVIEW_TITLE_MAX = 120;
export const REVIEW_BODY_MIN = 10;
export const REVIEW_BODY_MAX = 2000;

/** An approved review as `GET /api/product/:id/reviews` returns it. The author name is shortened ("Jane D."). */
export interface PublicReviewDto {
  _id: string;
  product_id: string;
  rating: number;
  title: string | null;
  review: string | null;
  is_verified: boolean;
  helpful_count: number;
  author_name: string;
  created_at: string;
}

export interface RatingSummary {
  average: number;
  count: number;
  /** Approved reviews per star, keys 5..1. */
  distribution: Record<1 | 2 | 3 | 4 | 5, number>;
}

/** `GET /api/product/:id/reviews?sort&page&length` → `data`. */
export interface ProductReviewsDto {
  result: PublicReviewDto[];
  count: number;
  page: number;
  length: number;
  sort: ReviewSort;
  summary: RatingSummary;
}

/** `GET /api/review/featured` items: approved reviews of live products, with the product. */
export interface FeaturedReviewDto extends PublicReviewDto {
  product: { _id: string; name: string; slug: string; thumbnail?: string | null };
}

/** The signed-in customer's own review (`GET /api/review?product_id=`), any status. */
export interface OwnReviewDto {
  _id: string;
  product_id: string;
  rating: number;
  title: string | null;
  review: string | null;
  is_verified: boolean;
  status: ReviewStatus;
  created_at: string;
}

/** A review in the back office (`GET /api/backoffice/review` with REVIEW_ADMIN_GRAPH). */
export interface AdminReviewDto {
  _id: string;
  product_id: string;
  customer_id: string;
  rating: number;
  title?: string | null;
  review?: string | null;
  is_verified?: boolean;
  helpful_count?: number;
  status: ReviewStatus;
  created_at: string;
  product?: { _id: string; name: string; slug?: string } | null;
  customer?: { _id: string; name?: string; phone?: string; email?: string } | null;
}

export const REVIEW_ADMIN_GRAPH = {
  "*": true,
  product: { _id: true, name: true, slug: true },
  customer: { _id: true, name: true, phone: true, email: true },
};

export const emptyRatingSummary = (): RatingSummary => ({
  average: 0,
  count: 0,
  distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
});

/** Share of approved reviews with `star` stars, 0..100. */
export const starShare = (summary: RatingSummary, star: 1 | 2 | 3 | 4 | 5): number =>
  summary.count ? Math.round((summary.distribution[star] / summary.count) * 100) : 0;
